/**
 * Phase 8 · C-21 — Pending sequence-recipient stash.
 *
 * Bridges the gap between `NextStepBridge` (Smart Match → "Create sequence")
 * and `SequencePage` (the surface that actually owns a sequence id we can
 * enroll against). The Smart Match chip stashes ticked candidate ids in
 * `sessionStorage`; once the user lands on a sequence detail page after
 * /sequences/new, `SequencePage` reads + clears the stash and invokes
 * `sequenceRecipientsService.enroll()`.
 *
 * sessionStorage was chosen intentionally — the handoff must not survive a
 * tab close, and we don't want stale candidate ids persisting across logins.
 */

const STORAGE_KEY = 'ggj.pendingSequenceRecipients.v1';

/** 15 minutes — long enough to click through the intake, short enough to be safe. */
const TTL_MS = 15 * 60 * 1000;

export interface PendingSequenceRecipients {
  /** Smart Match thread id the selection came from (used to correlate consumption). */
  threadId: string;
  /** Candidate UUIDs to enrol. */
  candidateIds: string[];
  /** Epoch ms when the stash was written. */
  stashedAt: number;
}

function readStash(): PendingSequenceRecipients | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingSequenceRecipients;
    if (
      !parsed ||
      typeof parsed.threadId !== 'string' ||
      !Array.isArray(parsed.candidateIds) ||
      typeof parsed.stashedAt !== 'number'
    ) {
      return null;
    }
    if (Date.now() - parsed.stashedAt > TTL_MS) {
      clearPendingRecipients();
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function stashPendingRecipients(threadId: string, candidateIds: string[]): void {
  const unique = Array.from(new Set(candidateIds.filter(Boolean)));
  if (unique.length === 0) {
    clearPendingRecipients();
    return;
  }
  try {
    const payload: PendingSequenceRecipients = {
      threadId,
      candidateIds: unique,
      stashedAt: Date.now(),
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* sessionStorage unavailable — fail silently. */
  }
}

/**
 * Peek without consuming. Used by consumers that want to know whether a
 * stash exists before deciding to act (e.g. "Include 3 selected candidates?").
 */
export function peekPendingRecipients(): PendingSequenceRecipients | null {
  return readStash();
}

/**
 * Atomically read + clear the stash. Returns `null` when none is present or
 * the stash is expired.
 */
export function consumePendingRecipients(): PendingSequenceRecipients | null {
  const stash = readStash();
  if (stash) clearPendingRecipients();
  return stash;
}

export function clearPendingRecipients(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
